import { finalAnswerText } from "@mathos-v2/domain";
import type {
  ClassroomEntry,
  MasteryEvidence,
  StudentAnswerPayload,
  TeacherTurnEnvelope,
  VerificationResult,
} from "./contracts";
import { findDemoLesson } from "./demo-lessons";
import {
  CHILD_ID,
  HOUSEHOLD_ID,
  type DemoSessionRecord,
} from "./demo-state";

function teacherTurn(
  sessionId: string,
  activityId: string,
  phase: TeacherTurnEnvelope["phase"],
  teacherText: string,
  question: string | undefined,
  stateIntent: TeacherTurnEnvelope["stateIntent"],
  boardOperations: TeacherTurnEnvelope["boardOperations"],
): TeacherTurnEnvelope {
  return {
    schemaVersion: "1",
    turnId: crypto.randomUUID(),
    sessionId,
    activityId,
    phase,
    teacherText,
    speechText: teacherText,
    boardOperations,
    visualOperations: [],
    ...(question ? { question } : {}),
    expectedResponse: {
      kind: question ? "math" : "none",
      description: question
        ? "写出判断，并用一句话说明核对依据。"
        : "听老师讲解并观察板书。",
    },
    stateIntent,
    citedKnowledgeAtomIds: ["L5-definition", "L5-method"],
    provider: {
      id: "demo-provider",
      model: "teacher-preview",
      requestId: `demo-${Date.now()}`,
    },
  };
}

function teacherEntry(turn: TeacherTurnEnvelope): ClassroomEntry {
  return {
    id: turn.turnId,
    kind: "teacher",
    phase: turn.phase,
    occurredAt: new Date().toISOString(),
    teacherTurn: {
      ...turn,
      visualOperations: turn.visualOperations ?? [],
    },
  };
}

export function createInitialRecord(lessonId: string): DemoSessionRecord {
  const lesson = findDemoLesson(lessonId);
  const now = new Date().toISOString();
  const sessionId = crypto.randomUUID();
  const firstActivity = lesson.activities[0]!;
  const currentIndex = lessonId === "L5" ? 1 : 2;
  const currentActivity =
    lesson.activities[currentIndex] ?? lesson.activities[0]!;
  const introduction = teacherTurn(
    sessionId,
    firstActivity.id,
    "explain",
    lessonId === "L5"
      ? "今天我们先把“集合”这件事说清楚。集合不是一个模糊的类别名称，而是一组能够逐个核对的对象。花括号里的每一个数，都是集合中的一个元素。"
      : `今天学习“${lesson.title}”。我们先讲清它研究的数学对象和成立条件，再看方法为什么有效。`,
    undefined,
    "advance",
    [
      {
        type: "write",
        blockId: "definition",
        format: "text",
        content:
          lessonId === "L5"
            ? "集合：能够逐个核对的一组对象"
            : `${lesson.title}：先看定义，再看成立条件`,
      },
    ],
  );
  const modeled = teacherTurn(
    sessionId,
    currentActivity.id,
    currentActivity.phase,
    lessonId === "L5"
      ? "看集合 A={3,6,9,12,15,18}。我先示范判断 9：不猜共同规律，只在花括号中逐个核对。看见 9 确实写在里面，所以写成 9 属于 A。现在换一个对象，由你来判断 12。"
      : `我先完整示范一次：从定义中找条件，再把题目中的对象逐项对应。现在轮到你完成其中最关键的一步。`,
    currentActivity.studentPrompt ?? `说出${lesson.title}的关键判断条件。`,
    "stay",
    [
      {
        type: "write",
        blockId: "example",
        format: "latex",
        content:
          lessonId === "L5"
            ? "A=\\{3,6,9,12,15,18\\}"
            : "\\text{定义}\\longrightarrow\\text{条件}\\longrightarrow\\text{判断}",
      },
      { type: "highlight", blockId: "example", tone: "focus" },
    ],
  );
  return {
    snapshot: {
      sessionId,
      householdId: HOUSEHOLD_ID,
      childId: CHILD_ID,
      lessonId,
      status: "active",
      phase: currentActivity.phase,
      activityIndex: currentIndex,
      version: 1,
      lastEventSequence: 2,
      updatedAt: now,
    },
    entries: [teacherEntry(introduction), teacherEntry(modeled)],
    evidence: [],
  };
}

export function normalizeDemoAnswer(answer: StudentAnswerPayload): string {
  return finalAnswerText(answer)
    .replace(/\s+/g, "")
    .toLowerCase();
}

export function verifyDemoAnswer(
  activityId: string,
  answer: StudentAnswerPayload,
): VerificationResult {
  const normalized = normalizeDemoAnswer(answer);
  let correct = false;
  if (activityId === "L5-model" || activityId === "L5-diagnose") {
    const relationDirectionIsValid =
      normalized.includes("12属于a") ||
      normalized.includes("12∈a") ||
      normalized.includes("12\\ina") ||
      normalized.includes("12在a中") ||
      normalized === "是";
    const usesInvalidReason =
      normalized.includes("因为12是偶数") ||
      normalized.includes("偶数所以") ||
      normalized.includes("因为是偶数");
    correct = relationDirectionIsValid && !usesInvalidReason;
  } else if (activityId === "L5-summary") {
    correct =
      normalized.length >= 6 &&
      (normalized.includes("核对") ||
        normalized.includes("集合") ||
        normalized.includes("里面"));
  } else if (activityId === "L5-transfer") {
    correct =
      normalized.includes("14") &&
      (normalized.includes("不属于") ||
        normalized.includes("不在") ||
        normalized.includes("∉") ||
        normalized.includes("\\notin"));
  } else {
    correct = normalized.length >= 4;
  }
  return {
    outcome: correct ? "correct" : "incorrect",
    normalizedAnswer: normalized,
    reasons: correct
      ? ["回答包含当前数学对象，并给出了可核对的关系。"]
      : ["当前回答还没有同时写清对象、关系和判断依据。"],
    matchedClaims: correct ? ["当前活动的关键结构"] : [],
    missingClaims: correct ? [] : ["具体对象与判断依据"],
    forbiddenClaims: [],
    mayAdvance: correct,
    needsTeacherClarification: !correct,
    verifier: "demo-typed-verifier",
  };
}

export function createNextTeacherTurn(
  record: DemoSessionRecord,
  answer: StudentAnswerPayload,
  verification: VerificationResult,
): TeacherTurnEnvelope {
  const lesson = findDemoLesson(record.snapshot.lessonId);
  const current = lesson.activities[record.snapshot.activityIndex]!;
  const studentWords = displayDemoAnswer(answer);
  if (!verification.mayAdvance) {
    if (current.independence === "independent") {
      return teacherTurn(
        record.snapshot.sessionId,
        current.id,
        current.phase,
        "这次还不能确认你已经独立掌握。我不补提示，也不指出具体错在何处；请从题目条件开始完整检查后，再独立作答一次。",
        current.studentPrompt,
        "stay",
        [],
      );
    }
    return teacherTurn(
      record.snapshot.sessionId,
      current.id,
      current.phase,
      `我看见你刚才写的是“${trimSentenceEnd(studentWords) || "还没写完整"}”。先不急着改答案：这里要判断的是具体对象 12 与集合 A 的关系。关系左边应该是一个数，右边才是集合。`,
      "请重新看 A={3,6,9,12,15,18}：12 在花括号里吗？把完整关系写出来。",
      "clarify",
      [{ type: "highlight", blockId: "example", tone: "caution" }],
    );
  }
  if (current.id === "L5-model" || current.id === "L5-diagnose") {
    return teacherTurn(
      record.snapshot.sessionId,
      "L5-summary",
      "fade_scaffold",
      "这一步写稳了。你不是凭“像不像偶数”来猜，而是回到集合中逐个核对；这正是判断属于关系最可靠的方法。",
      "现在请你用自己的话概括：怎样判断一个具体对象是否属于某个集合？",
      "advance",
      [
        {
          type: "write",
          blockId: "method",
          format: "text",
          content: "具体对象 → 逐个核对 → 写出属于关系",
        },
      ],
    );
  }
  if (current.id === "L5-summary") {
    return teacherTurn(
      record.snapshot.sessionId,
      "L5-transfer",
      "independent_transfer",
      "方法已经由你自己说清楚了。下面换一组数，陈老师不再提示步骤；你独立完成判断并留下理由。",
      "设 B={4,8,12,16,20}，判断 14 与 B 的关系，并写出理由。",
      "advance",
      [{ type: "clear", blockId: "example" }],
    );
  }
  return teacherTurn(
    record.snapshot.sessionId,
    current.id,
    "summary",
    "你独立完成了新的对象判断，也写出了核对依据。今天真正学会的不是一个符号，而是先认清对象，再用集合中的成员逐个验证关系。",
    undefined,
    "complete",
    [{ type: "highlight", blockId: "method", tone: "correct" }],
  );
}

function displayDemoAnswer(answer: StudentAnswerPayload): string {
  return finalAnswerText(answer).trim();
}

function trimSentenceEnd(value: string): string {
  return value.replace(/[。！？!?；;，,\s]+$/gu, "");
}

export function nextDemoActivityIndex(
  record: DemoSessionRecord,
  verification: VerificationResult,
): number {
  if (!verification.mayAdvance) return record.snapshot.activityIndex;
  const lesson = findDemoLesson(record.snapshot.lessonId);
  const currentId = lesson.activities[record.snapshot.activityIndex]!.id;
  if (currentId === "L5-model" || currentId === "L5-diagnose") return 3;
  if (currentId === "L5-summary") return 4;
  return record.snapshot.activityIndex;
}

export function createTransferEvidence(
  record: DemoSessionRecord,
  answer: StudentAnswerPayload,
  verification: VerificationResult,
): MasteryEvidence {
  return {
    evidenceId: crypto.randomUUID(),
    sessionId: record.snapshot.sessionId,
    childId: CHILD_ID,
    lessonId: record.snapshot.lessonId,
    activityId: answer.activityId,
    attemptNumber: 1,
    kind: "independent_transfer",
    studentAnswer: answer,
    verification,
    teacherHelpAfterPrompt: false,
    recordedAt: new Date().toISOString(),
  };
}
